import {
  type QueryParam,
  type UrlStateConfig,
  getFirstParamValue,
  parsePage,
  parseSortColumn,
  parseSortDirection,
} from "metabase/common/hooks/use-url-state";
import type {
  ListTaskRunsSortColumn,
  SortDirection,
  SortingOptions,
  TaskRunDateFilterOption,
  TaskRunEntityType,
  TaskRunStatus,
  TaskRunType,
} from "metabase-types/api";

import {
  guardTaskRunEntityType,
  guardTaskRunRunType,
  guardTaskRunStartedAtRange,
  guardTaskRunStatus,
} from "../../utils";

export const TASK_RUN_SORT_COLUMNS = [
  "started_at",
  "ended_at",
  "run_type",
  "status",
  "entity_name",
  "task_count",
] satisfies readonly ListTaskRunsSortColumn[];

const DEFAULT_SORT_COLUMN: ListTaskRunsSortColumn = "started_at";
const DEFAULT_SORT_DIRECTION = "desc";

export const DEFAULT_SORTING: SortingOptions<ListTaskRunsSortColumn> = {
  sort_column: DEFAULT_SORT_COLUMN,
  sort_direction: DEFAULT_SORT_DIRECTION,
};

type UrlState = {
  page: number;
  sort_column: ListTaskRunsSortColumn;
  sort_direction: SortDirection;
  "run-type": TaskRunType | null;
  "entity-type": TaskRunEntityType | null;
  "entity-id": number | null;
  status: TaskRunStatus | null;
  "started-at": TaskRunDateFilterOption | null;
  "include-today": boolean;
};

export const urlStateConfig: UrlStateConfig<UrlState> = {
  parse: (query) => {
    // entity-type and entity-id only filter meaningfully as a pair (the picker
    // shows a value only when both are set, and the backend needs the type to
    // interpret the id), so drop both when either is missing.
    const entityType = parseTaskRunEntityType(query["entity-type"]);
    const entityId = parseTaskRunEntityId(query["entity-id"]);
    const hasEntityPair = entityType !== null && entityId !== null;

    return {
      page: parsePage(query.page),
      sort_column: parseSortColumn(
        query.sort_column,
        TASK_RUN_SORT_COLUMNS,
        DEFAULT_SORT_COLUMN,
      ),
      sort_direction: parseSortDirection(
        query.sort_direction,
        DEFAULT_SORT_DIRECTION,
      ),
      "run-type": parseTaskRunRunType(query["run-type"]),
      "entity-type": hasEntityPair ? entityType : null,
      "entity-id": hasEntityPair ? entityId : null,
      status: parseTaskRunStatus(query.status),
      "started-at": parseTaskRunStartedAt(query["started-at"]),
      "include-today": parseIncludeToday(query["include-today"]),
    };
  },
  serialize: ({
    page,
    sort_column,
    sort_direction,
    "run-type": runType,
    "entity-type": entityType,
    "entity-id": entityId,
    status,
    "started-at": startedAt,
    "include-today": includeToday,
  }) => ({
    page: page === 0 ? undefined : String(page),
    sort_column: sort_column === DEFAULT_SORT_COLUMN ? undefined : sort_column,
    sort_direction:
      sort_direction === DEFAULT_SORT_DIRECTION ? undefined : sort_direction,
    "run-type": runType === null ? undefined : runType,
    "entity-type": entityType === null ? undefined : entityType,
    "entity-id": entityId === null ? undefined : String(entityId),
    status: status === null ? undefined : status,
    "started-at": startedAt === null ? undefined : startedAt,
    "include-today": includeToday ? "true" : undefined,
  }),
};

const parseTaskRunRunType = (param: QueryParam): UrlState["run-type"] => {
  const value = getFirstParamValue(param);
  return value && guardTaskRunRunType(value) ? value : null;
};

const parseTaskRunEntityType = (param: QueryParam): UrlState["entity-type"] => {
  const value = getFirstParamValue(param);
  return value && guardTaskRunEntityType(value) ? value : null;
};

const parseTaskRunEntityId = (param: QueryParam): UrlState["entity-id"] => {
  const value = getFirstParamValue(param);
  if (!value) {
    return null;
  }
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseTaskRunStatus = (param: QueryParam): UrlState["status"] => {
  const value = getFirstParamValue(param);
  return value && guardTaskRunStatus(value) ? value : null;
};

const parseTaskRunStartedAt = (param: QueryParam): UrlState["started-at"] => {
  const value = getFirstParamValue(param);
  return value && guardTaskRunStartedAtRange(value) ? value : null;
};

const parseIncludeToday = (param: QueryParam): UrlState["include-today"] => {
  return getFirstParamValue(param) === "true";
};
